import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useCommentsRealtime, Comment } from 'hooks/useCommentsRealtime';
import { usePresence } from 'hooks/usePresence';
import { useThread } from './ThreadContext';
import { getAuth } from 'firebase/auth';
import { EditorMode } from '../types';

export interface FeedbackForChat {
  comment: Comment;
  replies: Comment[];
  imageUrl: string | null;
  coordinates: { x: number; y: number } | null;
  savedPosition?: { historyIndex: number; selectedImageInGroup: number };
}

interface FeedbackContextValue {
  // State
  isAnnotationMode: boolean;
  pendingAnnotation: { x: number; y: number } | null;
  setPendingAnnotation: (annotation: { x: number; y: number } | null) => void;
  highlightedCommentId: string | null;
  isFeedbackPanelExpanded: boolean;
  setIsFeedbackPanelExpanded: (expanded: boolean) => void;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  pendingFeedbackForChat: FeedbackForChat | null;
  setPendingFeedbackForChat: (feedback: FeedbackForChat | null) => void;

  // Data from hooks
  comments: any[];
  activeUsers: any[];
  currentUserEmail: string;

  // Actions
  handleAddComment: (message: string, pictureId?: string, coordinates?: { x: number; y: number }, parentCommentId?: string) => Promise<void>;
  handleHighlightAnnotation: (commentId: string | null) => void;
  handleClearAnnotation: () => void;
  deleteComment: (commentId: string, userId: string) => Promise<void>;
  toggleReaction: (commentId: string, emoji: string, userId: string) => Promise<void>;
  toggleResolve: (commentId: string, userId: string) => Promise<void>;

  // Mutable refs for cross-context communication (set by VideoContext)
  _editorModeRef: React.MutableRefObject<EditorMode>;
  _videoElRef: React.MutableRefObject<HTMLVideoElement | null>;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export const useFeedback = (): FeedbackContextValue => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within a FeedbackProvider');
  return ctx;
};

interface FeedbackProviderProps {
  children: React.ReactNode;
  db: any;
  uid: string;
}

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children, db, uid }) => {
  const { currentProjectId, currentThreadId } = useThread();

  // State
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [pendingAnnotation, setPendingAnnotation] = useState<{ x: number; y: number } | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [isFeedbackPanelExpanded, setIsFeedbackPanelExpanded] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [pendingFeedbackForChat, setPendingFeedbackForChat] = useState<FeedbackForChat | null>(null);

  // Mutable refs for cross-context communication — VideoContext (a child) sets these
  const _editorModeRef = useRef<EditorMode>('picture');
  const _videoElRef = useRef<HTMLVideoElement | null>(null);

  // Get current user email
  const currentUserEmail = getAuth().currentUser?.email || '';

  // Use feedback hooks
  const { comments, addComment, deleteComment, toggleReaction, toggleResolve } = useCommentsRealtime(db, currentProjectId, currentThreadId);
  const { activeUsers } = usePresence(db, currentProjectId, currentThreadId, uid, currentUserEmail);

  // Automatically manage annotation mode
  useEffect(() => {
    setIsAnnotationMode(isFeedbackPanelExpanded && !replyingTo);
  }, [replyingTo, isFeedbackPanelExpanded]);

  // Clear highlighted annotations and pending annotation when feedback panel is closed
  useEffect(() => {
    if (!isFeedbackPanelExpanded) {
      setHighlightedCommentId(null);
      setPendingAnnotation(null);
    }
  }, [isFeedbackPanelExpanded]);

  // Helper to format video timestamp
  const formatVideoTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddComment = useCallback(
    async (message: string, pictureId?: string, coordinates?: { x: number; y: number }, parentCommentId?: string) => {
      let finalMessage = message;

      if (_editorModeRef.current === 'video' && _videoElRef.current && !parentCommentId) {
        const timestamp = formatVideoTimestamp(_videoElRef.current.currentTime);
        finalMessage = `[${timestamp}] ${message}`;
      }

      await addComment(finalMessage, uid, currentUserEmail, pictureId, coordinates, parentCommentId);
    },
    [addComment, uid, currentUserEmail]
  );

  const handleHighlightAnnotation = useCallback((commentId: string | null) => {
    setHighlightedCommentId(commentId);
  }, []);

  const handleClearAnnotation = useCallback(() => {
    setPendingAnnotation(null);
  }, []);

  return (
    <FeedbackContext.Provider value={{
      isAnnotationMode,
      pendingAnnotation,
      setPendingAnnotation,
      highlightedCommentId,
      isFeedbackPanelExpanded,
      setIsFeedbackPanelExpanded,
      replyingTo,
      setReplyingTo,
      pendingFeedbackForChat,
      setPendingFeedbackForChat,
      comments,
      activeUsers,
      currentUserEmail,
      handleAddComment,
      handleHighlightAnnotation,
      handleClearAnnotation,
      deleteComment,
      toggleReaction,
      toggleResolve,
      _editorModeRef,
      _videoElRef,
    }}>
      {children}
    </FeedbackContext.Provider>
  );
};
